import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from "recharts";
import { EmptyState } from "./EmptyState";
import { FpChart } from "../charts/FpChart";
import { PGoldChart } from "../charts/PGoldChart";
import { EthPrice, FpPrice, GasPrice, PGoldPrice, TreasuryBalance } from "../../../common/types";
import { GasChart } from "../charts/GasChart";
import { EthChart } from "../charts/EthChart";
import { TreasuryChart } from "../charts/TreasuryChart";

export function Stats({
  speciesCounts,
  walletAddress,
  fpPrices,
  pGoldPrices,
  isPhone,
  ethPrices,
  gasPrices,
  treasuryBalances,
  isDarkMode,
}: {
  speciesCounts: { species: string; count: number }[] | null;
  walletAddress: string;
  ethPrices: EthPrice[];
  pGoldPrices: PGoldPrice[];
  isPhone: boolean;
  gasPrices: GasPrice[];
  fpPrices: FpPrice[];
  treasuryBalances: TreasuryBalance[];
  isDarkMode: boolean;
}) {
  const isLoading = !speciesCounts && !!walletAddress;
  if (isLoading)
    return (
      <div>
        <EmptyState isDarkMode={isDarkMode} isLoading={isLoading} isStats={true} />
      </div>
    );

  const COLORS = isDarkMode
    ? [
        "#37b764",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#AF19FF",
        "#FF4560",
        "#d46b2f",
        "#95cfec",
        "#37b764",
      ]
    : [
        "#342e2e",
        "#00C49F",
        "#FFBB28",
        "#FF8042",
        "#AF19FF",
        "#FF4560",
        "#d46b2f",
        "#95cfec",
        "#342e2e",
      ];

  const data = Array.isArray(speciesCounts)
    ? speciesCounts
        .filter((entry) => entry?.species && typeof entry.count === "number")
        .map(({ species, count }) => ({
          name: species.slice(1).charAt(0).toUpperCase() + species.slice(2),
          value: count,
        }))
        .sort((a, b) => b.value - a.value)
    : [];

  const totalPets = data.reduce((sum, pet) => sum + pet.value, 0);

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mr-6 flex justify-center">
        <div
          className={`ml-2 mt-4 flex w-72 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
        >
          <div className="flex flex-row">Pet Species</div>
          <img
            src={isDarkMode ? "/imgs/darkpaw.svg" : "/imgs/paw.svg"}
            alt="paw"
            className="ml-2 mt-[.38rem] h-4 w-4"
          />
        </div>
      </div>
      <div className="flex flex-row justify-center">
        <div
          className={`my-2 ml-4 mr-4 flex w-[80%] flex-col items-center justify-center rounded-lg border-2  p-4 text-sm ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
        >
          <div className="flex w-full justify-between">
            <div className="mb-4 font-sans text-xl">Pets Alive • {totalPets}</div>
          </div>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={data} layout="horizontal" margin={{ top: 20 }}>
              <XAxis dataKey="name" stroke={isDarkMode ? "#37b764" : "#342e2e"} tick={false} />
              <YAxis stroke={isDarkMode ? "#37b764" : "#342e2e"} />
              <Bar dataKey="value" fill={isDarkMode ? "#37b764" : "#342e2e"}>
                {data.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
                <LabelList dataKey="value" position="top" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {data.map((entry, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="mr-2 h-3 w-3 rounded-full"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></div>
                <span>{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={`mt-8 border-b-2 ${isDarkMode ? "border-darkgreen" : "border-brown"}`}></div>
      <div className="flex flex-col justify-center">
        <div className="flex justify-center">
          <div
            className={`ml-2 mt-4 flex w-72 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
          >
            <div className="flex flex-row">FP Charts</div>
            <img
              src={isDarkMode ? "/imgs/passBadgeDark.png" : "/imgs/passBadge.png"}
              alt=""
              className="ml-2 mt-[.38rem] h-4 w-4"
            />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center">
          <FpChart isDarkMode={isDarkMode} fpPrices={fpPrices} />
          <PGoldChart
            isDarkMode={isDarkMode}
            pGoldPrices={pGoldPrices}
            fpPrice={fpPrices[fpPrices.length - 1]}
            isPhone={isPhone}
          />
          <TreasuryChart isDarkMode={isDarkMode} treasuryBalances={treasuryBalances} />
        </div>
      </div>
      <div className={`mt-8 border-b-2 ${isDarkMode ? "border-darkgreen" : "border-brown"}`}></div>
      <div className="mr-6 flex flex-col justify-center">
        <div className="flex justify-center">
          <div
            className={`mb-4 ml-2 mt-4 flex w-72 flex-row justify-center rounded-lg border-[1.5px] px-2 py-1 text-lg ${isDarkMode ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0-2px_#37b764]" : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"}`}
          >
            <div className="flex flex-row">Crypto Charts</div>
            <img
              src={isDarkMode ? "/imgs/chartDark.svg" : "/imgs/chart.svg"}
              alt=""
              className="ml-2 mt-[.38rem] h-4 w-4"
            />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center">
          <GasChart isDarkMode={isDarkMode} gasPrices={gasPrices} />
          <EthChart isDarkMode={isDarkMode} ethPrices={ethPrices} />
        </div>
      </div>
    </div>
  );
}
